import { isRedirectingToLogin } from 'api/accessToken';
import { DotLoader } from 'components/DotLoader';
import { ErrorBoundary } from 'components/ErrorBoundary/ErrorBoundary';
import type { FC } from 'react';
import { useEffect } from 'react';
import { useRouteError } from 'react-router';

function isModuleNotFoundError(error: Error): boolean {
  // chrome: "Failed to fetch dynamically imported module: http://localhost:5173/src/routes/posts.index.tsx?tsr-split"
  // firefox: "error loading dynamically imported module: http://localhost:5173/src/routes/posts.index.tsx?tsr-split"
  // safari: "Importing a module script failed."
  if (typeof error?.message !== 'string') return false;

  return (
    error.message.startsWith('Failed to fetch dynamically imported module') ||
    error.message.startsWith('error loading dynamically imported module') ||
    error.message.startsWith('Importing a module script failed')
  );
}

const ThrowRouteError: FC<{ error: unknown }> = ({ error }) => {
  throw error instanceof Error ? error : new Error('Unknown routing error');
};

export const RouteErrorElement: FC = () => {
  'use memo';

  const error = useRouteError();
  const isModuleLoadError = isModuleNotFoundError(error as Error);
  const storageKey = `route_reload:${(error as Error).message}`;
  const attemptReload = isModuleLoadError && !isRedirectingToLogin && !sessionStorage.getItem(storageKey);

  useEffect(() => {
    if (attemptReload) {
      sessionStorage.setItem(storageKey, '1');
      window.location.reload();
    }
  }, [attemptReload, storageKey]);

  // Requests and dynamic imports aborted by the login redirect are not real errors.
  if (attemptReload || isRedirectingToLogin) {
    return <DotLoader fullScreen />;
  }

  return (
    <ErrorBoundary>
      <ThrowRouteError error={error} />
    </ErrorBoundary>
  );
};
